<template>
  <div class="container">
    <div class="title-container">
      <span class="title">贡献时长分析</span>
      <a-tag color="blue" style="margin-left: 8px;">非华为</a-tag>
    </div>
    <a-spin :spinning="!nonHuaweiAvgDays">
      <div style="display: flex;">
        <div class="avg-time-card">
          <div style="margin-bottom: 12px;">
            <clock-circle-outlined style="color: white; margin-right: 8px; height: 10px" />
            <span class="avg-time-title">
              平均贡献时长
            </span>
          </div>
          <div>
            <span class="avg-time-number">
              {{ nonHuaweiAvgDays }}
            </span>
            <span class="avg-time-unit">
              天
            </span>
          </div>
        </div>
        <div class="avg-time-description">
          <p style="font-size: 16px; font-weight: 700; color:black">数据说明</p>
          <p>
            <span style="font-weight: bold; color: black;">非华为</span>贡献者在
            OpenHarmony 项目（主干、SIG仓、TPC仓）的平均持续贡献时间为
            <span style="color: #1E6CFF; font-weight: 700;">{{ nonHuaweiAvgDays }}
              天</span>，反映了社区开发者的持续投入程度和参与度水平。这一指标有助于评估社区的活跃度和贡献者的稳定性。
          </p>
        </div>
      </div>
    </a-spin>

    <div style="margin-top: 24px; display: flex; justify-content: space-between;">
      <div>
        <rise-outlined class="rise-icon" />
        <span class="title">持续共建分析</span>
        <a-tag color="blue" style="margin-left: 8px;">非华为</a-tag>
      </div>
      <div style="color: #6B7280; font-size: 14px;">
        总计 {{ formatNumber(totalContributors) }} 个贡献者
      </div>
    </div>

    <div style="padding: 16px">
      <a-spin :spinning="cumulativeSpinning">
        <div class="contrib-box-row">
          <div v-for="box in cumulativeBoxes" :key="box.key" class="contrib-box" :style="{ borderColor: box.color }">
            <div class="box-line1">
              <span class="box-dot" :style="{ color: box.color }">●</span>
              <span class="box-label">{{ box.label }}</span>
            </div>
            <div class="box-line2">
              <user-outlined class="box-user-icon" />
              <span class="box-number">{{ formatNumber(box.count) }}</span>
            </div>
            <div class="box-line3">个贡献者</div>
          </div>
        </div>
        <div style="margin-top: 16px;">
          <MyBarChart :categories="barCategories" :values="barValues" :colors="barColors" :barWidth="120"
            barCategoryGap="10%" />
        </div>
      </a-spin>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { ClockCircleOutlined, RiseOutlined, UserOutlined } from '@ant-design/icons-vue';
import MyBarChart from '@/measurement-platform/components/MyBarChart.vue';
import axios from 'axios';
import { notification } from 'ant-design-vue';

const nonHuaweiAvgDays = ref(null);
const cumulativeContributorData = ref(null);
const cumulativeSpinning = ref(true);

const yearColorMap = {
  'year_1': '#3B82F6',
  'year_2': '#10B981',
  'year_3': '#F59E0B',
  'year_4': '#EF4444',
  'year_5': '#8B5CF6'
};

const cumulativeBoxes = computed(() => {
  // 保持固定顺序，避免接口字段顺序变化
  const order = [
    { key: 'year_1', years: 1 },
    { key: 'year_2', years: 2 },
    { key: 'year_3', years: 3 },
    { key: 'year_4', years: 4 },
    { key: 'year_5', years: 5 }
  ];
  return order.map(({ key, years }) => ({
    key,
    color: yearColorMap[key],
    label: years === 5 ? `累计共建 ≥5 年` : `累计共建 ${years} 年`,
    count: cumulativeContributorData.value?.[key]
  }));
});

const barCategories = computed(() => cumulativeBoxes.value.map(b => b.label));
const barValues = computed(() => cumulativeBoxes.value.map(b => b.count));
const barColors = computed(() => cumulativeBoxes.value.map(b => b.color));

const totalContributors = computed(() => {
  const d = cumulativeContributorData.value;
  if (!d) return null;
  return ['year_1', 'year_2', 'year_3', 'year_4', 'year_5']
    .reduce((sum, k) => sum + (Number(d[k]) || 0), 0);
});

const formatNumber = (n) => {
  if (n === null || n === undefined) return '';
  const num = typeof n === 'number' ? n : Number(n);
  return Number.isNaN(num) ? n : num.toLocaleString();
};

const fetchAvgDays = async () => {
  try {
    const res = await axios.get('/contributorController/getContributionDays', { params: { employer: 'NON_HUAWEI' } });
    nonHuaweiAvgDays.value = res?.data?.data;
  } catch (e) {
    nonHuaweiAvgDays.value = null;
    notification.error({ message: '平均贡献时长加载失败', description: e?.message || e });
  }
};

const fetchCumulative = async () => {
  cumulativeSpinning.value = true;
  try {
    const res = await axios.get('/contributorController/getCountYear', { params: { employer: 'NON_HUAWEI' } });
    cumulativeContributorData.value = res?.data?.data;
  } catch (e) {
    cumulativeContributorData.value = null;
    notification.error({ message: '累计共建数据加载失败', description: e?.message || e });
  } finally {
    cumulativeSpinning.value = false;
  }
};

onMounted(() => {
  fetchAvgDays();
  fetchCumulative();
});
</script>

<style scoped>
.container {
  text-align: left;
  margin-bottom: 20px;
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 16px 24px;
}

.title-container {
  padding: 16px 0;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: #111111;
}

.avg-time-card {
  flex: 1;
  align-items: center;
  padding: 24px;
  background-color: #1E6CFF;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(30, 108, 255, 0.18);
  transition: box-shadow 0.25s ease;
}

.avg-time-title {
  font-size: 14px;
  font-weight: 500;
  color: #FFFFFFEE;
}

.avg-time-number {
  font-size: 42px;
  font-weight: 700;
  color: #FFFFFF;
}

.avg-time-unit {
  font-size: 18px;
  font-weight: 500;
  color: #FFFFFFEE;
  margin-left: 4px;
}

.avg-time-description {
  flex: 1;
  min-width: 70%;
  border: 2px solid #E5E7EB;
  border-radius: 12px;
  font-size: 14px;
  color: #6B7280;
  line-height: 20px;
  margin-left: 24px;
  padding: 24px 24px 0 24px;
}

.contrib-box-row {
  padding: 0 24px;
  display: flex;
  gap: 16px;
}

.contrib-box {
  flex: 1;
  background-color: #F9FAFB;
  border: 2px solid;
  border-radius: 10px;
  padding: 12px;
  text-align: center;
}

.box-line1 {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  color: #111827;
}

.box-dot {
  font-family: 'MS Mincho', serif;
  font-size: 16px;
  line-height: 1;
}

.box-label {
  font-size: 14px;
}

.box-line2 {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.box-user-icon {
  color: #9CA3AF;
  font-size: 14px;
}

.box-number {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.box-line3 {
  margin-top: 4px;
  font-size: 12px;
  color: #6B7280;
}

.rise-icon {
  color: #00A63E;
  background-color: #DBFCE7;
  border-radius: 4px;
  width: 25px;
  height: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}
</style>
